import React, { useState, useRef, useEffect } from "react";
import "./styles/styles.css";
import "../../styles/global.css";
import AccreditationButton from "../AccreditationButton/AccreditationButton";
import { icons } from "../../constants";

const CustomImageCropper = ({
  localSrc,
  cropWidth = 300,
  cropHeight = 300,
  onCropComplete,
  onCloseClick,
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const prevSrcRef = useRef(null);

  const [baseImgDimensions, setBaseImgDimensions] = useState({
    width: 0,
    height: 0,
  });

  const [cropBox, setCropBox] = useState({
    x: 0,
    y: 0,
    size: cropWidth,
  });

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [initialCropBox, setInitialCropBox] = useState(null);

  const minSize = 100;
  // No fixed max here on purpose — the displayed image can render much
  // larger than any reasonable flat cap (up to viewport height minus 220px,
  // see .image-cropper-image-wrap), so a hardcoded ceiling like the 600px
  // this used to be stops the box well short of the actual image edges on
  // any reasonably large photo. The real ceiling is the image's own bounds,
  // computed per-drag below as maxSizeForBounds.

  const resetCropBox = () => {
    if (!imgRef.current) return;

    const baseWidth = imgRef.current.clientWidth;
    const baseHeight = imgRef.current.clientHeight;

    setBaseImgDimensions({ width: baseWidth, height: baseHeight });
    setCropBox({
      x: (baseWidth - cropWidth) / 2,
      y: (baseHeight - cropHeight) / 2,
      size: cropWidth,
    });

    setDragging(false);
    setResizing(false);
    setStartPos({ x: 0, y: 0 });
    setInitialCropBox(null);
  };

  useEffect(() => {
    if (prevSrcRef.current !== localSrc) {
      prevSrcRef.current = localSrc;
    }
    resetCropBox();
  }, [localSrc]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    setInitialCropBox({ ...cropBox });
  };

  const handleMouseMove = (e) => {
    if (!dragging && !resizing) return;

    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;

    if (dragging) {
      let newX = initialCropBox.x + deltaX;
      let newY = initialCropBox.y + deltaY;

      newX = Math.max(
        0,
        Math.min(baseImgDimensions.width - cropBox.size, newX),
      );
      newY = Math.max(
        0,
        Math.min(baseImgDimensions.height - cropBox.size, newY),
      );

      setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
    }

    if (resizing) {
      // The largest square that still fits the image from this fixed
      // top-left corner — clamp to it instead of bailing out on any
      // requested size that overflows, which used to freeze the box at
      // whatever size the previous mousemove happened to land on, short of
      // the actual edge.
      const maxSizeForBounds = Math.min(
        baseImgDimensions.width - initialCropBox.x,
        baseImgDimensions.height - initialCropBox.y,
      );

      const newSize = Math.max(
        minSize,
        Math.min(maxSizeForBounds, initialCropBox.size + deltaX),
      );

      setCropBox((prev) => ({ ...prev, size: newSize }));
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    setResizing(false);
  };

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, resizing]);

  const handleCrop = () => {
    if (!imgRef.current) return;
    const image = imgRef.current;

    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / baseImgDimensions.width;
    const scaleY = image.naturalHeight / baseImgDimensions.height;

    ctx.drawImage(
      image,
      cropBox.x * scaleX,
      cropBox.y * scaleY,
      cropBox.size * scaleX,
      cropBox.size * scaleY,
      0,
      0,
      800,
      800,
    );

    const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92);

    if (onCropComplete) {
      onCropComplete(croppedDataUrl);
      onCloseClick();
    }
  };

  return (
    <div className="flex-direction-row justify-center items-center image-cropper">
      <div className="flex-direction-column justify-between items-center image-cropper-content">
        <div className="flex-direction-row justify-between width-100">
          <img
            className="pointer"
            src={icons.close_gray}
            onClick={onCloseClick}
            alt="Close"
          />
        </div>

        {localSrc ? (
          <div
            ref={containerRef}
            className="image-cropper-image-wrap"
          >
            <img
              ref={imgRef}
              src={localSrc}
              alt="crop"
              onLoad={resetCropBox}
            />
            <div
              onMouseDown={handleMouseDown}
              style={{
                position: "absolute",
                left: cropBox.x,
                top: cropBox.y,
                width: cropBox.size,
                height: cropBox.size,
                border: "2px dashed red",
                cursor: "move",
                backgroundColor: "rgba(255,255,255,0.2)",
              }}
            >
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setResizing(true);
                  setStartPos({ x: e.clientX, y: e.clientY });
                  setInitialCropBox({ ...cropBox });
                }}
                style={{
                  position: "absolute",
                  width: "20px",
                  height: "20px",
                  right: "-10px",
                  bottom: "-10px",
                  background: "red",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          </div>
        ) : (
          <p>Slika ne postoji.</p>
        )}

        <AccreditationButton
          onClick={handleCrop}
          title="Crop"
          type="save"
          className="crop-btn"
        />
      </div>
    </div>
  );
};

export default CustomImageCropper;
